import React from 'react';
import LanguagePicker from 'src/components/LanguagePicker/LanguagePicker';
import {
  UI_CLIENTS,
  CONFIG_TYPE,
  PROGRAM_ACCOUNTS,
  BRANDING_LOGO,
  CASE_LIST_COLUMN,
  AccountId,
  DDM_SCHEMA_ID_FOR_PROGRAMS,
  SOCIAL_RESPONSIBILITY_AUDIT_ACCEPTED_FILES,
  DEFAULT_ACCEPTED_FILES,
  SPIM_MAX_TOTAL_ATTACHMENTS_SIZE,
  SPIM_MAX_NUMBER_OF_ATTACHMENTS,
} from 'src/constants/GlobalConstants';
import {
  defaultSupportedLocales,
  sustainabilitySupportedLocales,
  dsaOutOfCourtSettlementSupportedLocales,
  csamSupportedLocales,
  paymentComplaintSupportedLocales,
  spimSupportedLocales,
} from 'src/constants/LocalizationConstants';

import { HOME_ROUTE } from 'src/constants/UrlConstants';
/**
 * How to define the config.
 * ACCOUNT_ID: {
 *    WEBSITE_TYPE(VICI or RegulatorCentral) : {
 *      CONFIG_NAME: {
 *          //define the property here.
 *      }
 *    }
 * }
 */

const defaultProductSafetyConfig = {
  [UI_CLIENTS.VICI]: {
    [CONFIG_TYPE.LOCALIZATION]: {
      localizationEnabled: true,
      supportedLocales: defaultSupportedLocales,
    },
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId:
        DDM_SCHEMA_ID_FOR_PROGRAMS.PRODUCT_SAFETY_REGULATORY_INTAKE_EU,
    },
  },
  [UI_CLIENTS.RegulatorCentral]: {
    [CONFIG_TYPE.LOCALIZATION]: {
      localizationEnabled: true,
      supportedLocales: defaultSupportedLocales,
    },
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId:
        DDM_SCHEMA_ID_FOR_PROGRAMS.PRODUCT_SAFETY_REGULATORY_INTAKE_EU,
    },
  },
};

const defaultSocialResponsibilityAuditRequestConfig = {
  [UI_CLIENTS.VICI]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId:
        DDM_SCHEMA_ID_FOR_PROGRAMS.SOCIAL_RESPONSIBILITY_AUDIT_REQUESTS,
      allowedFileTypesForAttachments:
        SOCIAL_RESPONSIBILITY_AUDIT_ACCEPTED_FILES,
    },
    [CONFIG_TYPE.CASE_LIST]: {
      columns: [
        CASE_LIST_COLUMN.CASE_ID,
        CASE_LIST_COLUMN.CASE_TITLE,
        CASE_LIST_COLUMN.CREATED_ON,
        CASE_LIST_COLUMN.CREATED_BY_USER,
        CASE_LIST_COLUMN.LAST_UPDATED_ON,
        CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
        CASE_LIST_COLUMN.CASE_STATUS,
        CASE_LIST_COLUMN.ASSIGNEE,
      ],
    },
  },
  [UI_CLIENTS.RegulatorCentral]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId:
        DDM_SCHEMA_ID_FOR_PROGRAMS.SOCIAL_RESPONSIBILITY_AUDIT_REQUESTS,
      allowedFileTypesForAttachments:
        SOCIAL_RESPONSIBILITY_AUDIT_ACCEPTED_FILES,
    },
    [CONFIG_TYPE.CASE_LIST]: {
      columns: [
        CASE_LIST_COLUMN.CASE_ID,
        CASE_LIST_COLUMN.CASE_TITLE,
        CASE_LIST_COLUMN.CREATED_ON,
        CASE_LIST_COLUMN.CREATED_BY_USER,
        CASE_LIST_COLUMN.LAST_UPDATED_ON,
        CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
        CASE_LIST_COLUMN.CASE_STATUS,
        CASE_LIST_COLUMN.ASSIGNEE,
      ],
    },
  },
};

const defaultGtsConfig = {
  [UI_CLIENTS.VICI]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.GTS_BROKER_INTAKE,
    },
  },
  [UI_CLIENTS.RegulatorCentral]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.GTS_BROKER_INTAKE,
    },
  },
};

const defaultFoodSafetyNaConfig = {
  [UI_CLIENTS.VICI]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.FOOD_SAFETY_NA,
    },
  },
  [UI_CLIENTS.RegulatorCentral]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.FOOD_SAFETY_NA,
    },
  },
};

const defaultEprEuInternalConfig = {
  [UI_CLIENTS.VICI]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.EPR_EU_INTERNAL,
    },
  },
  [UI_CLIENTS.RegulatorCentral]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.EPR_EU_INTERNAL,
    },
  },
};

const defaultDsaOutOfCourtSettlementConfig = {
  [UI_CLIENTS.VICI]: {
    [CONFIG_TYPE.LOCALIZATION]: {
      localizationEnabled: true,
      supportedLocales: defaultSupportedLocales,
    },
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId:
        DDM_SCHEMA_ID_FOR_PROGRAMS.DSA_OUT_OF_COURT_SETTLEMENT,
    },
  },
  [UI_CLIENTS.RegulatorCentral]: {
    [CONFIG_TYPE.LOCALIZATION]: {
      localizationEnabled: true,
      supportedLocales: defaultSupportedLocales,
    },
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId:
        DDM_SCHEMA_ID_FOR_PROGRAMS.DSA_OUT_OF_COURT_SETTLEMENT,
    },
    [CONFIG_TYPE.FEATURE_FLAGS]: {
      showLeftPanel: false,
    },
  },
};

const defaultRiscCpscConfig = {
  [UI_CLIENTS.VICI]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.RISC_CPSC_NA,
    },
  },
  [UI_CLIENTS.RegulatorCentral]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.RISC_CPSC_NA,
    },
  },
};

const defaultSpimConfig = {
  [UI_CLIENTS.VICI]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.SPIM,
      maxTotalAttachmentsSize: SPIM_MAX_TOTAL_ATTACHMENTS_SIZE,
      maxNumberOfAttachments: SPIM_MAX_NUMBER_OF_ATTACHMENTS,
    },
    [CONFIG_TYPE.CASE_LIST]: {
      columns: [
        CASE_LIST_COLUMN.CASE_ID,
        CASE_LIST_COLUMN.PARAGON_CASE_IDS,
        CASE_LIST_COLUMN.CASE_TITLE,
        CASE_LIST_COLUMN.CREATED_ON,
        CASE_LIST_COLUMN.LAST_UPDATED_ON,
        CASE_LIST_COLUMN.CASE_STATUS,
      ],
    },
    [CONFIG_TYPE.LOCALIZATION]: {
      localizationEnabled: true,
      supportedLocales: spimSupportedLocales,
    },
  },
};

const defaultDsaPocConfig = {
  [UI_CLIENTS.VICI]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.DSA_POC,
    },
  },
  [UI_CLIENTS.RegulatorCentral]: {
    [CONFIG_TYPE.PROPERTIES]: {
      ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.DSA_POC,
    },
  },
};

/**
 * Generates the PSC Configuration.
 * @returns list of PSC configuration defined in AccountId.PRODUCT_SAFETY list
 */
export const pscConfiguration = (): { [key: string]: any } => {
  return AccountId.PRODUCT_SAFETY.reduce((accumulation, value) => {
    accumulation[value] = defaultProductSafetyConfig;
    return accumulation;
  }, {});
};

export const gtsConfiguration = (): { [key: string]: any } => {
  return AccountId.GTS.reduce((accumulation, value) => {
    accumulation[value] = defaultGtsConfig;
    return accumulation;
  }, {});
};

export const foodSafetyNaConfiguration = (): { [key: string]: any } => {
  return AccountId.FOOD_SAFETY_NA.reduce((accumulation, value) => {
    accumulation[value] = defaultFoodSafetyNaConfig;
    return accumulation;
  }, {});
};

export const eprEuInternalConfiguration = (): { [key: string]: any } => {
  return AccountId.EPR_EU_INTERNAL.reduce((accumulation, value) => {
    accumulation[value] = defaultEprEuInternalConfig;
    return accumulation;
  }, {});
};

export const riscCpscConfiguration = (): { [key: string]: any } => {
  return AccountId.RISC_CPSC_NA.reduce((accumulation, value) => {
    accumulation[value] = defaultRiscCpscConfig;
    return accumulation;
  }, {});
};

export const spimConfiguration = (): { [key: string]: any } => {
  return AccountId.SPIM.reduce((accumulation, value) => {
    accumulation[value] = defaultSpimConfig;
    return accumulation;
  }, {});
};

export const dsaPocConfiguration = (): { [key: string]: any } => {
  return AccountId.DSA_POC.reduce((accumulation, value) => {
    accumulation[value] = defaultDsaPocConfig;
    return accumulation;
  }, {});
};

export const socialResponsibilityAuditRequestConfiguration = (): {
  [key: string]: any;
} => {
  return AccountId.SOCIAL_RESPONSIBILITY_AUDIT_REQUESTS.reduce(
    (accumulation, value) => {
      accumulation[value] = defaultSocialResponsibilityAuditRequestConfig;
      return accumulation;
    },
    {},
  );
};

export const dsaOutOfCourtSettlementConfiguration = (): {
  [key: string]: any;
} => {
  return AccountId.DSA_OUT_OF_COURT_SETTLEMENT.reduce((accumulation, value) => {
    accumulation[value] = defaultDsaOutOfCourtSettlementConfig;
    return accumulation;
  }, {});
};

export const configStore = {
  DEFAULT: {
    [UI_CLIENTS.VICI]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: false,
        supportedLocales: [],
      },
      [CONFIG_TYPE.BRANDING]: {
        brandingLogoName: BRANDING_LOGO.AMAZON,
        logoRedirectUrl: `#${HOME_ROUTE}`,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: true,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
          CASE_LIST_COLUMN.CASE_STATUS,
        ],
      },
      [CONFIG_TYPE.PROPERTIES]: {
        allowedFileTypesForAttachments: DEFAULT_ACCEPTED_FILES,
      },
    },
    [UI_CLIENTS.RegulatorCentral]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: false,
        supportedLocales: [],
      },
      [CONFIG_TYPE.BRANDING]: {
        brandingLogoName: BRANDING_LOGO.AMAZON,
        logoRedirectUrl: `#${HOME_ROUTE}`,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: true,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
          CASE_LIST_COLUMN.CASE_STATUS,
        ],
      },
      [CONFIG_TYPE.PROPERTIES]: {
        allowedFileTypesForAttachments: DEFAULT_ACCEPTED_FILES,
      },
    },
  },
  [PROGRAM_ACCOUNTS.SUSTAINABLITY]: {
    [UI_CLIENTS.VICI]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: true,
        supportedLocales: sustainabilitySupportedLocales,
      },
      [CONFIG_TYPE.BRANDING]: {
        brandingLogoName: BRANDING_LOGO.AMAZON,
        logoRedirectUrl: `#${HOME_ROUTE}`,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: true,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
          CASE_LIST_COLUMN.CASE_STATUS,
          CASE_LIST_COLUMN.ASSIGNEE,
          CASE_LIST_COLUMN.CASE_LOCALE,
        ],
      },
      [CONFIG_TYPE.PROPERTIES]: {
        ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.SUSTAINABILITY,
      },
    },
    [UI_CLIENTS.RegulatorCentral]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: true,
        supportedLocales: sustainabilitySupportedLocales,
      },
      [CONFIG_TYPE.BRANDING]: {
        brandingLogoName: BRANDING_LOGO.AMAZON,
        logoRedirectUrl: `#${HOME_ROUTE}`,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: false,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
        ],
      },
      [CONFIG_TYPE.PROPERTIES]: {
        ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.SUSTAINABILITY,
      },
    },
  },
  [PROGRAM_ACCOUNTS.DSA_OOCS_ADRIOT_OOC_SETTLEMENT]: {
    [UI_CLIENTS.VICI]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: true,
        supportedLocales: dsaOutOfCourtSettlementSupportedLocales,
      },
      [CONFIG_TYPE.BRANDING]: {
        brandingLogoName: BRANDING_LOGO.AMAZON,
        logoRedirectUrl: `#${HOME_ROUTE}`,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: true,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
          CASE_LIST_COLUMN.CASE_STATUS,
          CASE_LIST_COLUMN.ASSIGNEE,
          CASE_LIST_COLUMN.CASE_LOCALE,
        ],
      },
    },
    [UI_CLIENTS.RegulatorCentral]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: true,
        supportedLocales: dsaOutOfCourtSettlementSupportedLocales,
      },
      [CONFIG_TYPE.BRANDING]: {
        brandingLogoName: BRANDING_LOGO.AMAZON,
        logoRedirectUrl: `#${HOME_ROUTE}`,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: false,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
        ],
      },
    },
  },
  [PROGRAM_ACCOUNTS.CSAM]: {
    [UI_CLIENTS.VICI]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: true,
        supportedLocales: csamSupportedLocales,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: true,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
          CASE_LIST_COLUMN.CASE_STATUS,
          CASE_LIST_COLUMN.ASSIGNEE,
          CASE_LIST_COLUMN.CASE_LOCALE,
        ],
      },
      [CONFIG_TYPE.PROPERTIES]: {
        ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.CSAM,
      },
    },
    [UI_CLIENTS.RegulatorCentral]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: true,
        supportedLocales: csamSupportedLocales,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: false,
      },
      [CONFIG_TYPE.MENU]: {
        rightMenuItems: [
          {
            id: '1',
            type: 'custom',
            icon: <LanguagePicker />,
            description: 'Localization Dropdown',
          },
        ],
      },
      [CONFIG_TYPE.PROPERTIES]: {
        ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.CSAM,
      },
    },
  },
  [PROGRAM_ACCOUNTS.PAYMENTS_SERVICES_COMPLAINT]: {
    [UI_CLIENTS.VICI]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: true,
        supportedLocales: paymentComplaintSupportedLocales,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: true,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
          CASE_LIST_COLUMN.CASE_STATUS,
          CASE_LIST_COLUMN.ASSIGNEE,
          CASE_LIST_COLUMN.CASE_LOCALE,
        ],
      },
      [CONFIG_TYPE.PROPERTIES]: {
        ddmIntakeFormSchemaId:
          DDM_SCHEMA_ID_FOR_PROGRAMS.PAYMENTS_SERVICES_COMPLAINT,
      },
    },
    [UI_CLIENTS.RegulatorCentral]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: true,
        supportedLocales: paymentComplaintSupportedLocales,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: false,
      },
      [CONFIG_TYPE.PROPERTIES]: {
        ddmIntakeFormSchemaId:
          DDM_SCHEMA_ID_FOR_PROGRAMS.PAYMENTS_SERVICES_COMPLAINT,
      },
    },
  },
  [PROGRAM_ACCOUNTS.TRUSTED_CONSUMER]: {
    [UI_CLIENTS.VICI]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: false,
        supportedLocales: [],
      },
      [CONFIG_TYPE.BRANDING]: {
        brandingLogoName: BRANDING_LOGO.AMAZON,
        logoRedirectUrl: `#${HOME_ROUTE}`,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: true,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
          CASE_LIST_COLUMN.CASE_STATUS,
        ],
      },
      [CONFIG_TYPE.PROPERTIES]: {
        ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.TRUSTED_CONSUMER,
      },
    },
    [UI_CLIENTS.RegulatorCentral]: {
      [CONFIG_TYPE.LOCALIZATION]: {
        localizationEnabled: false,
        supportedLocales: [],
      },
      [CONFIG_TYPE.BRANDING]: {
        brandingLogoName: BRANDING_LOGO.AMAZON,
        logoRedirectUrl: `#${HOME_ROUTE}`,
      },
      [CONFIG_TYPE.FEATURE_FLAGS]: {
        showLeftPanel: false,
      },
      [CONFIG_TYPE.CASE_LIST]: {
        columns: [
          CASE_LIST_COLUMN.CASE_ID,
          CASE_LIST_COLUMN.CASE_TITLE,
          CASE_LIST_COLUMN.CREATED_ON,
          CASE_LIST_COLUMN.CREATED_BY_USER,
          CASE_LIST_COLUMN.LAST_UPDATED_ON,
          CASE_LIST_COLUMN.LAST_UPDATED_BY_USER,
        ],
      },
      [CONFIG_TYPE.PROPERTIES]: {
        ddmIntakeFormSchemaId: DDM_SCHEMA_ID_FOR_PROGRAMS.TRUSTED_CONSUMER,
      },
    },
  },
  ...pscConfiguration(),
  ...gtsConfiguration(),
  ...socialResponsibilityAuditRequestConfiguration(),
  ...dsaOutOfCourtSettlementConfiguration(),
  ...foodSafetyNaConfiguration(),
  ...eprEuInternalConfiguration(),
  ...riscCpscConfiguration(),
  ...spimConfiguration(),
  ...dsaPocConfiguration(),
};
